import React, { useState } from 'react';
import { Box, Card, CardContent, Typography, Button } from '@mui/material';
import { LAYOUT } from '../../../theme';
import { StorageCleanupDialog } from '../components/StorageCleanupDialog';
import { OrphanedUserDataDialog } from '../components/OrphanedUserDataDialog';

type CleanupMode = 'orphan-entities' | 'unused-files' | 'old-variants' | null;

export const CleanupSettings: React.FC = () => {
  const [cleanupMode, setCleanupMode] = useState<CleanupMode>(null);
  const [orphanUserDataOpen, setOrphanUserDataOpen] = useState(false);

  const handleOpenCleanup = (
    mode: 'orphan-entities' | 'unused-files' | 'old-variants'
  ) => {
    setCleanupMode(mode);
  };

  const handleCloseCleanup = () => {
    setCleanupMode(null);
  };

  return (
    <Box sx={{ py: 3, maxWidth: LAYOUT.mediumContentWidth, margin: '0 auto' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Storage Cleanup */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Storage Cleanup
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Scan and clean up unused files and folders from storage.
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Orphan Entities Cleanup */}
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Clean Up Orphaned Folders
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Remove story and show folders that no longer exist in the
                  database. This happens when entities are deleted but their
                  storage folders remain.
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => handleOpenCleanup('orphan-entities')}
                >
                  Scan for Orphaned Folders
                </Button>
              </Box>

              {/* Non-Conventional Files Cleanup */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Clean Up Non-Conventional Files
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Remove files that don&apos;t follow the conventional naming
                  from media-config (e.g., voice.mp3, default.png). This helps
                  clean up old or incorrectly named files.
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => handleOpenCleanup('unused-files')}
                >
                  Scan for Non-Conventional Files
                </Button>
              </Box>

              {/* Old Format Variants Cleanup */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Clean Up Old Format Variants
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Remove old format variations of conventional files (e.g.,
                  default.webp, default.70.png) while keeping the current
                  conventional files (e.g., default.png). This cleans up files
                  from previous uploads or generations.
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => handleOpenCleanup('old-variants')}
                >
                  Scan for Old Format Variants
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Orphaned User Data */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Orphaned User Data
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Remove rows in user-keyed tables that are not linked to auth.users
              (user_events, user_listened_stories, user_show_prefs,
              user_muted_shows, exposures) whose user no longer exists. These are
              leftovers from an interrupted self-service account deletion.
            </Typography>
            <Button
              variant="contained"
              onClick={() => setOrphanUserDataOpen(true)}
            >
              Scan &amp; Delete Orphaned User Rows
            </Button>
          </CardContent>
        </Card>
      </Box>

      {/* Cleanup Dialog */}
      {cleanupMode && (
        <StorageCleanupDialog
          open={cleanupMode !== null}
          mode={cleanupMode}
          onClose={handleCloseCleanup}
        />
      )}

      <OrphanedUserDataDialog
        open={orphanUserDataOpen}
        onClose={() => setOrphanUserDataOpen(false)}
      />
    </Box>
  );
};
